import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { useTiersGameContext } from '../context/TiersGameContext';
import { useUserAndGameContext } from '../../../contexts/UserAndGameContext';
import { useTheme } from '@emotion/react';
import useMediaQuery from '@mui/material/useMediaQuery';
import TiersPicksCard from './TiersPicksCard';

const TiersPicksByTier = ({ tournamentLeaderboardData, leaderboardData, interval, currentRound }) => {
    const {
        pickState,
    } = useUserAndGameContext();

    const {
        tiersData,
    } = useTiersGameContext();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Count player picks for the current interval - wrapped in useCallback
    const countPlayerPicks = useCallback((playerId) => {
        if (!leaderboardData || !playerId) return 0;
        
        let pickCount = 0;
        leaderboardData?.forEach(user => {
            // Find the matching interval data for this user
            const intervalData = user?.intervalData?.find(int => int.interval === interval);
            
            if (intervalData) {
                // Check if any of this user's picks match the player ID
                const hasPlayerPick = intervalData.players.some(
                    player => player.id === playerId.toString()
                );
                
                if (hasPlayerPick) {
                    pickCount++;
                }
            }
        });
        
        return pickCount;
    }, [leaderboardData, interval]);

    const thisIntervalsTiersData = useMemo(() => 
        tiersData?.find((tiersData) => Number(tiersData.interval) === Number(interval)),
        [tiersData, interval]
    );
    
    const cardColors = useMemo(() => ({
        backgroundColor: thisIntervalsTiersData?.backgroundColor,
        textColor: thisIntervalsTiersData?.textColor
    }), [thisIntervalsTiersData]);

    // Add tournament data to each player in the tiers
    const enhancedTiersData = useMemo(() => {
        if (!thisIntervalsTiersData?.tiers?.length || !tournamentLeaderboardData?.Players) return thisIntervalsTiersData;
        
        // Create a deep copy of the tiers data
        const enhancedTiers = JSON.parse(JSON.stringify(thisIntervalsTiersData));
        
        // Add tournament data to each player
        enhancedTiers.tiers = enhancedTiers.tiers.map(tier => {
            if (!tier.players) return tier;
            
            tier.players = tier.players.map(player => {
                // Find the player in tournament data
                const tournamentPlayer = tournamentLeaderboardData.Players.find(
                    tp => String(tp.PlayerID) === String(player.id)
                );
                
                if (tournamentPlayer) {
                    // Add tournament data
                    player.tournamentData = tournamentPlayer;
                    // Add pick count
                    player.pickCount = countPlayerPicks(player.id);
                }
                
                return player;
            });
            
            return tier;
        });
        
        return enhancedTiers;
    }, [thisIntervalsTiersData, tournamentLeaderboardData, countPlayerPicks]);

    const tiersCards = useMemo(() => {
        if (!enhancedTiersData?.tiers?.length) return null;
    
        return enhancedTiersData.tiers.map((tier, index) => {
            return (
                <TiersPicksCard
                    key={index}
                    tier={tier}
                    backgroundColor={cardColors.backgroundColor}
                    textColor={cardColors.textColor}
                    interval={interval}
                    selectedPlayers={pickState}
                    lastCard={index === enhancedTiersData.tiers.length - 1}
                    currentRound={currentRound}
                />
            );
        });
    }, [enhancedTiersData, cardColors, interval, pickState, currentRound]); 

    return (
        <Box>
            {tiersCards}
        </Box>
    );
};

export default TiersPicksByTier;